import useAuth from '@app/auth/use-auth';
import { BulkActionBar } from '@app/components/data-table/bulk-edit/bulk-action-bar';
import { makeSelectColumn } from '@app/components/data-table/bulk-edit/select-column';
import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { toPickerPeriods } from '@app/components/multi-period-picker';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { makeUpdateItemsAction } from '@app/components/item-edit/update-items-modal-body';
import {
  OperationReportTypeEnum,
  PackagingOperationActivitiesList,
  PaginatedPackagingOperationActivitiesListList,
} from '@shared/api/types';
import { useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { soldGoodsRoutes } from '../../sold-goods-routes';
import {
  PackagingFilterColumn,
  packagingFilterKeys,
  PackagingFilterOption,
  packagingFilters,
} from '../filters';
import { useSoldPackagingTableColumns } from './sold-packaging-table-columns';
import SoldPackagingTableToolbar from './sold-packaging-table-toolbar';

interface SoldPackagingTableProps {
  data?: PaginatedPackagingOperationActivitiesListList;
  pageSize?: number;
  status?: string;
  isLoading?: boolean;
  isPreviousData?: boolean;
  isFetching?: boolean;
}

export default function SoldPackagingTable({
  pageSize,
  status,
  data,
  isLoading,
  isFetching,
}: SoldPackagingTableProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { count } = data || {};
  const pageCount = count ? Math.ceil(count / (pageSize || 10)) : 0;

  const filterOptions = useServerFilterOptions(
    packagingFilterKeys,
    data?.filters || {}
  );

  const useFilterOptions = useMemo(() => {
    const opts: FilterOptions<PackagingFilterOption[]> = {} as FilterOptions<
      PackagingFilterOption[]
    >;
    packagingFilterKeys.forEach((key) => {
      opts[key] = filterOptions[`${key}Filter`];
    });
    return opts;
  }, [filterOptions]);

  const { table, context } = useServerDataTable({
    data: data?.results || [],
    columns: [
      // Selection checkboxes are staff-only (bulk actions are gated to staff).
      ...(user?.isStaff
        ? [makeSelectColumn<PackagingOperationActivitiesList>()]
        : []),
      ...useSoldPackagingTableColumns({
        filters: useFilterOptions,
        timePeriods: toPickerPeriods(data?.filters?.timePeriods),
      }),
    ],
    pageCount,
    shallow: false,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        remoteId: false,
        [PackagingFilterColumn.Period]: false,
        [PackagingFilterColumn.Site]: false,
        [PackagingFilterColumn.Customer]: false,
        [PackagingFilterColumn.ActivitiesImport]: false,
        [PackagingFilterColumn.Tag]: false,
      },
      columnOrder: ['select', 'remoteId', 'packaging'],
    },
  });

  // "Update items" hides its own "Packaging" option since these items are
  // already packaging. Rows are keyed by activity uuid; change-type acts on
  // the SoldGood.
  const bulkActions = useMemo(
    () => [
      makeUpdateItemsAction({
        currentType: 'packaging',
        reportType: OperationReportTypeEnum.sales,
        resolveUuids: (table) =>
          table
            .getFilteredSelectedRowModel()
            .rows.map(
              (row) =>
                (row.original as PackagingOperationActivitiesList).soldGoodInfo
                  .uuid
            ),
      }),
    ],
    []
  );

  return (
    <>
      <DataTable
        table={table}
        context={context}
        tableLoading={!data && status === 'loading'}
        isLoading={isLoading || isFetching}
        className="bg-gray-00 text-left"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) =>
          navigate(
            generatePath(soldGoodsRoutes.SOLD_PACKAGING_ITEM, {
              item_uuid: row?.original.uuid,
            })
          )
        }
      >
        <DataTableToolbar
          table={table}
          filterOrder={[
            'packagingName',
            ...Object.values(packagingFilters).map((f) => f.column),
          ]}
        >
          <SoldPackagingTableToolbar />
        </DataTableToolbar>
      </DataTable>
      <BulkActionBar table={table} actions={bulkActions} />
    </>
  );
}
